import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { darkTheme } from "../../utils/Theme.js";
import { Bio } from "../../data/constants.js";

// Animations
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const glitchEffect = keyframes`
  0% { transform: translate(0); }
  20% { transform: translate(-5px, 5px); }
  40% { transform: translate(-5px, -5px); }
  60% { transform: translate(5px, 5px); }
  80% { transform: translate(5px, -5px); }
  100% { transform: translate(0); }
`;

// Styled Components
const HeroContainer = styled.section`
  background: ${darkTheme.bg};
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 80px 30px;

  @media (max-width: 960px) {
    padding: 60px 16px;
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
        circle at 15% 50%,
        ${darkTheme.primary}20 0%,
        transparent 25%
      ),
      radial-gradient(
        circle at 85% 30%,
        ${darkTheme.primary}15 0%,
        transparent 25%
      );
    z-index: 1;
  }
`;

const GridBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(
      rgba(133, 76, 230, 0.1) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(133, 76, 230, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  z-index: 1;
  perspective: 500px;
  transform-style: preserve-3d;
  transform: rotateX(60deg) translateY(-100px) translateZ(-100px);
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  z-index: 3;
  position: relative;

  @media (max-width: 960px) {
    flex-direction: column-reverse;
    text-align: center;
    gap: 40px;
  }
`;

const TextContent = styled.div`
  flex: 1;
  max-width: 600px;
  animation: ${fadeInUp} 0.8s ease-out forwards;

  @media (max-width: 960px) {
    max-width: 100%;
  }
`;

const ImageContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
`;

const Greeting = styled.div`
  color: ${darkTheme.primary};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.2s forwards;
  font-family: "Space Grotesk", sans-serif;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const Name = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  color: ${darkTheme.text_primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.4s forwards;
  font-family: "Space Grotesk", sans-serif;
  position: relative;
  display: inline-block;

  &:hover {
    animation: ${glitchEffect} 0.5s linear;
  }

  span {
    color: ${darkTheme.primary};
    position: relative;

    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 8px;
      background: linear-gradient(90deg, ${darkTheme.primary}, transparent);
      opacity: 0.5;
      z-index: -1;
      border-radius: 4px;
    }
  }

  @media (max-width: 960px) {
    font-size: 3rem;
  }

  @media (max-width: 640px) {
    font-size: 2.5rem;
  }
`;

const RoleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.6s forwards;
  font-family: "Space Grotesk", sans-serif;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const StaticText = styled.span`
  font-size: 1.5rem;
  font-weight: 500;
  color: ${darkTheme.text_primary};

  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`;

const DynamicText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${darkTheme.primary};
  min-width: 220px;
  text-align: left;

  @media (max-width: 640px) {
    font-size: 1.25rem;
    min-width: 180px;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${darkTheme.text_secondary};
  margin-bottom: 2.5rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.8s forwards;
  font-family: "Space Grotesk", sans-serif;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 16px 32px;
  background: linear-gradient(135deg, ${darkTheme.primary}, #6b2fcf);
  color: ${darkTheme.white};
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(133, 76, 230, 0.3);
  position: relative;
  overflow: hidden;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 1s forwards;
  font-family: "Space Grotesk", sans-serif;
  border: none;
  cursor: pointer;
  z-index: 2;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #6b2fcf, ${darkTheme.primary});
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(133, 76, 230, 0.5);

    &:before {
      opacity: 1;
    }
  }

  @media (max-width: 640px) {
    padding: 14px 28px;
    font-size: 1rem;
  }
`;

const ImageContainer = styled.div`
  width: 380px;
  height: 380px;
  position: relative;
  border-radius: 26px;
  overflow: hidden;
  animation: ${pulse} 8s infinite ease-in-out;

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }

  @media (max-width: 640px) {
    width: 250px;
    height: 250px;
  }
`;

const HexagonMask = styled.div`
  width: 100%;
  height: 100%;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background: linear-gradient(
    135deg,
    ${darkTheme.primary}30,
    ${darkTheme.primary}10
  );
  position: absolute;
  z-index: 1;
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit; /* match container */
  z-index: 2;
  position: relative;

  /* base look */
  filter: grayscale(15%) brightness(98%) contrast(108%);
  transition: all 0.5s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);

  &:hover {
    filter: grayscale(0%) brightness(105%) contrast(115%);
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.35);
  }
`;

const ImageGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 26px;
  padding: 3px; /* border thickness */
  background: linear-gradient(135deg, ${darkTheme.primary}, #6b2fcf, #9d5cff);
  background-size: 300% 300%;
  animation: ${gradientShift} 8s ease infinite;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none; /* let clicks pass through */
  z-index: 1;
`;

const ImageBorder = styled.div`
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background: linear-gradient(135deg, ${darkTheme.primary}, #6b2fcf);
  animation: ${gradientShift} 5s ease infinite;
  z-index: 0;
`;

const TechPillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 1.2s forwards;
`;

const TechPill = styled.span`
  padding: 8px 18px;
  background: ${darkTheme.card};
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${darkTheme.text_primary};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid ${darkTheme.primary}30;
  font-family: "Space Grotesk", sans-serif;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(133, 76, 230, 0.3);
    border: 1px solid ${darkTheme.primary};
  }
`;

const FloatingOrbs = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${darkTheme.primary};
  opacity: 0.5;
  filter: blur(5px);
  animation: ${floatAnimation} 8s infinite ease-in-out;
  z-index: 2;

  &:nth-child(1) {
    top: 20%;
    left: 10%;
    width: 30px;
    height: 30px;
    animation-delay: 0s;
  }

  &:nth-child(2) {
    top: 60%;
    right: 15%;
    width: 25px;
    height: 25px;
    animation-delay: 1s;
  }

  &:nth-child(3) {
    bottom: 30%;
    left: 20%;
    width: 15px;
    height: 15px;
    animation-delay: 2s;
  }
`;

// TypeWriter component for animation
const TypeWriter = ({ strings }) => {
  const [text, setText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStringIndex, setCurrentStringIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentString = strings[currentStringIndex];

    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          // Typing phase
          if (currentIndex < currentString.length) {
            setText(currentString.substring(0, currentIndex + 1));
            setCurrentIndex(currentIndex + 1);
          } else {
            // Wait before starting to delete
            setTimeout(() => setIsDeleting(true), 1500);
          }
        } else {
          // Deleting phase
          if (currentIndex > 0) {
            setText(currentString.substring(0, currentIndex - 1));
            setCurrentIndex(currentIndex - 1);
          } else {
            setIsDeleting(false);
            // Move to next string
            setCurrentStringIndex((currentStringIndex + 1) % strings.length);
          }
        }
      },
      isDeleting ? 50 : 100
    );

    return () => clearTimeout(timeout);
  }, [currentIndex, currentStringIndex, isDeleting, strings]);

  return <>{text}</>;
};

// Main Hero Component
const HeroSection = () => {
  return (
    <HeroContainer id="about">
      <GridBackground />
      <FloatingOrbs />
      <FloatingOrbs />
      <FloatingOrbs />

      <ContentWrapper>
        <TextContent>
          <Greeting>Hello, I'm</Greeting>
          <Name>
            <span>{Bio.name}</span>
          </Name>

          <RoleContainer>
            <StaticText>I am a</StaticText>
            <DynamicText>
              <TypeWriter strings={Bio.roles} />
            </DynamicText>
          </RoleContainer>

          <Description>{Bio.description}</Description>

          <CTAButton
            href={Bio.resume}
            target="_blank"
            rel="noopener noreferrer"
          >
            Review My Resume
          </CTAButton>

          <TechPillContainer>
            <TechPill>Full-Stack Development</TechPill>
            <TechPill>Backend Engineering</TechPill>
            <TechPill>System Design</TechPill>
            <TechPill>AI Applications</TechPill>
          </TechPillContainer>
        </TextContent>

        <ImageContent>
          <ImageContainer>
            <ImageGlow />
            <MainImage
              src={require("../../images/HeroImage.jpg")}
              alt={Bio.name}
            />
          </ImageContainer>
        </ImageContent>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroSection;
